import React, { useRef, useState, useContext, useEffect, useCallback } from "react";
import { AuthContext } from "../../../context/AuthContext";
import config from "../../../config";
import "./CustomizationHome.css";
import { MdDeleteForever, MdEdit, MdAddCircle, MdIosShare, MdSearch } from "react-icons/md";
import Loader from "../../../components/loader/loader";
import FieldsModal from "../../../components/field/FieldsModal";

// Import the sub-components
import CreateCustomization from "../Create/CreateCustomization";
import EditCustomization from "../Edit/EditCustomization";

const CustomizationHome = () => {
    const { user, logout } = useContext(AuthContext);
    const token = localStorage.getItem("access_token");
    const errorRef = useRef(null);

    // View State: 'list', 'create', 'edit', 'fields'
    const [view, setView] = useState("list");
    const [editingId, setEditingId] = useState(null);

    const [customizations, setCustomizations] = useState([]);
    const [defaultTemplates, setDefaultTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState(null);
    // Removed showFieldsModal state as it is now a view

    const [showShareModal, setShowShareModal] = useState(false);
    const [shareTargetId, setShareTargetId] = useState(null);
    const [shareEmail, setShareEmail] = useState("");
    const [shareError, setShareError] = useState(null);
    const [shareStatus, setShareStatus] = useState(null);
    const [loaderText, setLoaderText] = useState("Loading Customizations...");
    const [listSearchTerm, setListSearchTerm] = useState("");

    const loadInitialData = useCallback(async () => {
        setLoaderText("Loading Customizations...");
        setLoading(true);
        try {
            const [customsRes, activeRes, defaultsRes] = await Promise.all([
                fetch(`${config.REACT_APP_API_URL}/customization/customizations`, {
                    headers: { Authorization: token },
                }),
                fetch(`${config.REACT_APP_API_URL}/customization/active_customization/${user.id}`, {
                    headers: { Authorization: token },
                }),
                fetch(`${config.REACT_APP_API_URL}/customization/defaults`, {
                    headers: { Authorization: token },
                }),
            ]);

            const readResponse = async (response, { allowNotFound = false } = {}) => {
                const data = await response.json().catch(() => ({}));
                if (allowNotFound && response.status === 404) return null;
                if (!response.ok) {
                    const requestError = new Error(data.detail || data.message || `Request failed (${response.status})`);
                    requestError.status = response.status;
                    throw requestError;
                }
                return data;
            };

            const [allCustomizations, activeCustomization, defaults] = await Promise.all([
                readResponse(customsRes),
                readResponse(activeRes, { allowNotFound: true }),
                readResponse(defaultsRes),
            ]);

            if (!Array.isArray(allCustomizations) || !Array.isArray(defaults)) {
                throw new Error("The customization service returned an invalid response.");
            }

            const enrichedCustomizations = allCustomizations.map((c) => ({
                ...c,
                active: activeCustomization && c.id === activeCustomization.id,
            }));

            const defaultCustomization = enrichedCustomizations.find((c) => c.user_id === 0);
            const userCustomizations = enrichedCustomizations.filter((c) => c.user_id !== 0);

            setCustomizations(defaultCustomization ? [defaultCustomization, ...userCustomizations] : userCustomizations);
            setDefaultTemplates(defaults);
        } catch (err) {
            if (err.status === 401) {
                logout();
                return;
            }
            setError(`Initialization failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [token, user.id, logout]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    const toggleCustomization = async (id, willBeActive) => {
        const liveDefault = defaultTemplates.find((template) => template.active)
            || customizations.find((customization) => customization.user_id === 0);
        const targetId = willBeActive ? id : liveDefault?.id;
        if (!targetId) {
            setError("No live default customization is configured.");
            return;
        }
    
        try {
            await fetch(`${config.REACT_APP_API_URL}/customization/activate/${targetId}`, {
                method: "PUT",
                headers: { Authorization: token },
            });
    
            setCustomizations(prev =>
                prev.map(c => ({
                    ...c,
                    active: c.id === targetId,
                }))
            );
        } catch (err) {
            setError(`Activation failed: ${err.message}`);
        }
    };

    const confirmDelete = (id) => {
        setItemToDeleteId(id);
        setShowConfirmation(true);
    };

    const handleDelete = async () => {
        try {
            setLoaderText("Deleting customization...");
            setLoading(true);
            await fetch(`${config.REACT_APP_API_URL}/customization/${itemToDeleteId}`, {
                method: "DELETE",
                headers: { Authorization: token },
            });

            await loadInitialData();
        } catch (err) {
            setError(`Delete failed: ${err.message}`);
        } finally {
            setLoading(false);
            setShowConfirmation(false);
            setItemToDeleteId(null);
        }
    };

    const cloneTemplate = async (templateId) => {
        if (!templateId) return;
        try {
            setLoaderText("Cloning template...");
            setLoading(true);
            const res = await fetch(`${config.REACT_APP_API_URL}/customization/clone/${templateId}`, {
                method: "POST",
                headers: { Authorization: token },
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.detail || "Clone failed");
            }

            await loadInitialData();
        } catch (err) {
            setError(`Clone failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const shareTemplate = async (id) => {
        if (!shareEmail) {
            setShareError("Email is required.");
            return;
        }

        try {
            setLoaderText("Sharing customization...");
            setLoading(true);
            const res = await fetch(
                `${config.REACT_APP_API_URL}/customization/share/${id}?email=${encodeURIComponent(shareEmail)}`,
                {
                    method: "POST",
                    headers: { Authorization: token },
                }
            );

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || "Failed to share customization");
            }

            setShareStatus("Shared successfully!");
            setTimeout(() => {
                setShowShareModal(false);
                setShareEmail("");
                setShareError(null);
                setShareStatus(null);
                setShareTargetId(null);
            }, 1500);
        } catch (err) {
            setShareError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (error && errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [error]);

    // --- VIEW SWITCHING LOGIC ---
    if (view === "create") {
        return (
            <CreateCustomization 
                onBack={() => setView("list")} 
                onSuccess={() => {
                    setView("list");
                    loadInitialData();
                }}
            />
        );
    }

    if (view === "edit" && editingId) {
        return (
            <EditCustomization 
                customizationId={editingId}
                onBack={() => {
                    setView("list");
                    setEditingId(null);
                }} 
                onSuccess={() => {
                    setView("list");
                    setEditingId(null);
                    loadInitialData();
                }}
            />
        );
    }
    
    // NEW: Handle the 'fields' view
    if (view === "fields") {
        return (
            <FieldsModal 
                token={token} 
                onClose={() => setView("list")} 
                isPage={true} 
            />
        );
    }

    if (loading) {
        return <div className="loader-container"><Loader text={loaderText}/></div>;
    }

    const activeCustomization = customizations.find((c) => c.active);
    const filteredCustomizations = customizations.filter(({ name }) =>
        name.toLowerCase().includes(listSearchTerm.toLowerCase())
    );
    const editableCount = customizations.filter((c) => c.user_id !== 0).length;

    return (
        <div className="customization-content-wrapper">
            
            <div className="content-body animate-fade-in">
                <div className="customization-list-header">
                    <div>
                        <h1>Customizations</h1>
                        <div className="compact-meta-row">
                            <span>Active: {activeCustomization?.name || "None"}</span>
                            <span>{editableCount} saved</span>
                            <span>{defaultTemplates.length} defaults</span>
                        </div>
                    </div>
                    <div className="toolbar-actions header-actions">
                        <button className="primary-button compact-btn" onClick={() => setView("create")}>
                            Create New
                        </button>
                        <button onClick={() => setView("fields")} className="secondary-button compact-btn">
                            Fields
                        </button>
                    </div>
                </div>

                {error && (
                    <div ref={errorRef} className="error-banner">
                        {error}
                        <button onClick={() => setError("")} className="close-banner-btn">×</button>
                    </div>
                )}

                <div className="customization-control-panel">
                    <div className="search-input-wrap">
                        <MdSearch className="search-icon" />
                        <input
                            type="search"
                            className="text-input search-input"
                            placeholder="Search customizations"
                            value={listSearchTerm}
                            onChange={(e) => setListSearchTerm(e.target.value)}
                        />
                    </div>

                    {defaultTemplates.length > 1 && (
                        <div className="template-clone-control">
                            <label className="input-label">Start from template</label>
                            <select
                                className="select-input"
                                onChange={(e) => {
                                    const selectedId = parseInt(e.target.value, 10);
                                    if (selectedId) cloneTemplate(selectedId);
                                }}
                                defaultValue=""
                            >
                                <option value="" disabled>Choose a default...</option>
                                {defaultTemplates
                                    .filter(template => !template.active)
                                    .map(template => (
                                        <option key={template.id} value={template.id}>
                                            {template.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    )}
                </div>
                
                {/* Table Section */}
                <div className="table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th style={{width: '80px'}}>Active</th>
                                <th>Template Name</th>
                                <th style={{textAlign: 'right'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCustomizations.map(({ id, name, active, user_id }) => (
                                <tr key={id}>
                                    <td>
                                        <label className="ios-switch">
                                            <input
                                                type="checkbox"
                                                checked={active}
                                                onChange={() => toggleCustomization(id, !active)}
                                                disabled={user_id === 0 && active}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </td>
                                    <td className="val-col font-medium">{name}</td>
                                    <td>
                                        <div className="action-buttons-row">
                                            {user_id !== 0 && <button 
                                                className="icon-btn-styled" 
                                                onClick={() => {
                                                    setEditingId(id);
                                                    setView("edit");
                                                }}
                                                title="Edit this customization"
                                            >
                                                <MdEdit />
                                                <span className="btn-text">Edit</span>
                                            </button>}
                                            
                                            <button className="icon-btn-styled" onClick={() => cloneTemplate(id)} title="Clone this customization">
                                                <MdAddCircle />
                                                <span className="btn-text">Clone</span>
                                            </button>
                                            
                                            <button 
                                                className="icon-btn-styled" 
                                                onClick={() => {
                                                    setShareTargetId(id);
                                                    setShowShareModal(true);
                                                    setShareEmail("");
                                                    setShareError(null);
                                                    setShareStatus(null);
                                                }}
                                                title="Share this customization with another REDP user"
                                            >
                                                <MdIosShare />
                                                <span className="btn-text">Share</span>
                                            </button>
                                            
                                            {user_id !== 0 && <button 
                                                className="icon-btn-styled danger" 
                                                onClick={() => confirmDelete(id)}
                                                title="Delete this customization"
                                            >
                                                <MdDeleteForever />
                                                <span className="btn-text">Delete</span>
                                            </button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredCustomizations.length === 0 && (
                                <tr>
                                    <td colSpan="3" className="empty-table-cell">No customizations match your search.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            </div>

            {/* Share Modal */}
            {showShareModal && (
                <div className="provider-overlay animate-fade-in" onClick={() => setShowShareModal(false)}>
                    <div className="provider-window share-window" onClick={e => e.stopPropagation()}>
                        <button className="provider-close-btn" onClick={() => setShowShareModal(false)}>×</button>
                        <div className="provider-body-scroll center-content">
                            <h2>Share Customization</h2>
                            <p className="modal-desc">Enter the email address of the user you want to share this template with.</p>
                            
                            <div className="input-group" style={{ margin: '2rem 0' }}>
                                <input
                                    type="email"
                                    className="text-input"
                                    placeholder="user@example.com"
                                    value={shareEmail}
                                    onChange={(e) => setShareEmail(e.target.value)}
                                />
                            </div>

                            {shareError && <div className="error-message-inline center-text">{shareError}</div>}
                            {shareStatus && <div className="save-status-text success center-text">{shareStatus}</div>}
                            
                            <div className="share-action-container">
                                <button className="primary-button" onClick={() => shareTemplate(shareTargetId)} disabled={!shareEmail}>
                                    Share Template
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showConfirmation && (
                <div className="provider-overlay animate-fade-in">
                    <div className="provider-window delete-window">
                        <div className="provider-body-scroll center-content">
                            <h2 className="text-danger">Delete Customization?</h2>
                            <p className="modal-desc">This action cannot be undone. Are you sure you want to proceed?</p>
                            
                            <div className="delete-actions">
                                <button onClick={handleDelete} className="primary-button danger-bg">Yes, Delete</button>
                                <button onClick={() => setShowConfirmation(false)} className="secondary-button">Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomizationHome;
